import { useEffect, useState } from "react"

export function checkDistanceEffect({
  machineLat,
  machineLng,
  setDistanceError,
  setDistanceOk
}: {
  machineLat: number | string
  machineLng: number | string
  setDistanceError: (v: boolean) => void
  setDistanceOk: (v: boolean | null) => void
}) {
  let cancelled = false
  let intervalId: number | undefined

  // reset when machine coords change
  setDistanceError(false)
  setDistanceOk(null)

  const checkDistance = () => {
    const lat = typeof machineLat === 'string' ? Number(machineLat) : machineLat
    const lng = typeof machineLng === 'string' ? Number(machineLng) : machineLng

    if (lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) {
      if (!cancelled) {
        setDistanceError(true)
        setDistanceOk(false)
      }
      return
    }

    if (!navigator.geolocation) {
      if (!cancelled) {
        setDistanceError(true)
        setDistanceOk(false)
      }
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return

        const toRad = (v: number) => (v * Math.PI) / 180
        const earthRadiusMeters = 6371000

        const lat1Rad = toRad(lat)
        const lat2Rad = toRad(pos.coords.latitude)
        const deltaLatRad = toRad(pos.coords.latitude - lat)
        const deltaLngRad = toRad(pos.coords.longitude - lng)

        const sinDeltaLat = Math.sin(deltaLatRad / 2)
        const sinDeltaLng = Math.sin(deltaLngRad / 2)

        const a =
          sinDeltaLat * sinDeltaLat +
          Math.cos(lat1Rad) * Math.cos(lat2Rad) * sinDeltaLng * sinDeltaLng

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        const distanceMeters = earthRadiusMeters * c

        const ok = distanceMeters < 1000
        setDistanceOk(ok)
        setDistanceError(!ok)
      },
      () => {
        if (cancelled) return
        setDistanceError(true)
        setDistanceOk(false)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }

  // run immediately, then refresh every 5 seconds
  checkDistance()
  intervalId = window.setInterval(checkDistance, 5000)

  return () => {
    cancelled = true
    if (intervalId !== undefined) window.clearInterval(intervalId)
  }
}

export function useCheckDistance(
  machineLat?: number | string | null,
  machineLng?: number | string | null
) {
  const [distanceError, setDistanceError] = useState(false)
  const [distanceOk, setDistanceOk] = useState<boolean | null>(null)

  useEffect(() => {
    // machine not loaded yet -> "unknown" state, do not start interval
    if (machineLat == null || machineLng == null) {
      setDistanceError(false)
      setDistanceOk(null)
      return
    }

    return checkDistanceEffect({
      machineLat,
      machineLng,
      setDistanceError,
      setDistanceOk
    })
  }, [machineLat, machineLng])

  return [distanceError, distanceOk] as const
}